import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import InputNombre from '../components/InputNombre';
import TicketVente from './TicketVente';
import { offlineService, estErreurReseau } from '../services/offline.service';
import './Caisse.css';

interface Produit {
  id: number;
  nom: string;
  codeBarres: string;
  prixOrdinaire: number;
  prixRevendeur: number;
  prixTechnicien: number;
  stockActuel: number;
  tvaApplicable: boolean;
  image?: string;
}

// Retire les photos (base64) avant la mise en cache hors-ligne : elles alourdiraient
// localStorage sans être nécessaires pour encaisser. La caisse fonctionne sans.
const sansImages = (liste: Produit[]) => liste.map(({ image, ...p }) => p);

interface Client {
  id: number;
  nom: string;
  prenom: string;
  telephone: string;
  type: string;
  soldeCredit: number;
  plafondCredit: number;
}

interface LignePanier {
  produitId: number;
  produitNom: string;
  produitCode: string;
  prixUnitaire: number;
  quantite: number;
  remise: number;
  sousTotal: number;
  tvaApplicable: boolean;
}

const MODES_PAIEMENT = [
  { val: 'especes', label: '💵 Espèces' },
  { val: 'orange_money', label: '🟠 Orange Money' },
  { val: 'moov_money', label: '🟡 Moov Money' },
  { val: 'wave', label: '🔵 Wave' },
  { val: 'credit', label: '💳 Crédit client' },
];

const fmt = (n: number) => Math.round(Number(n)).toLocaleString('fr-FR');

export default function Caisse() {
  const [produits, setProduits] = useState<Produit[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [panier, setPanier] = useState<LignePanier[]>([]);
  const [rechercheProduit, setRechercheProduit] = useState('');
  const [rechercheClient, setRechercheClient] = useState('');
  const [clientSelectionne, setClientSelectionne] = useState<Client | null>(null);
  const [nomClientManuel, setNomClientManuel] = useState('');
  const [modeClient, setModeClient] = useState<'passage' | 'manuel' | 'existant'>('passage');
  const [showClientsDropdown, setShowClientsDropdown] = useState(false);
  const [typeClient, setTypeClient] = useState<'ordinaire' | 'revendeur' | 'technicien'>('ordinaire');
  const [modePaiement, setModePaiement] = useState('especes');
  const [tvaActive, setTvaActive] = useState(true);
  const [remiseGlobale, setRemiseGlobale] = useState(0);
  const [montantRecu, setMontantRecu] = useState(0);
  const [notes, setNotes] = useState('');
  const [showTicket, setShowTicket] = useState(false);
  const [derniereVente, setDerniereVente] = useState<any>(null);
  // Préférence persistée « imprimer automatiquement » (case cochée durablement).
  const [autoImprimer, setAutoImprimer] = useState(() => localStorage.getItem('nexacom_autoprint') === '1');
  // Déclencheur transitoire d'impression pour LE ticket courant (évite la
  // réimpression si on rouvre le dernier ticket via F12).
  const [imprimerAuto, setImprimerAuto] = useState(false);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState('');
  const [remises, setRemises] = useState({ revendeur: 15, technicien: 22 });
  const [showRaccourcis, setShowRaccourcis] = useState(false);
  const rechercheRef = useRef<HTMLInputElement>(null);

  // Désencombrement : menu client compact, options avancées repliées, remises par
  // ligne masquées par défaut.
  const [showClientMenu, setShowClientMenu] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [remisesOuvertes, setRemisesOuvertes] = useState<Set<number>>(new Set());

  const changerAutoImprimer = (v: boolean) => {
    setAutoImprimer(v);
    localStorage.setItem('nexacom_autoprint', v ? '1' : '0');
  };

  const basculerRemiseLigne = (produitId: number) => {
    setRemisesOuvertes(prev => {
      const s = new Set(prev);
      s.has(produitId) ? s.delete(produitId) : s.add(produitId);
      return s;
    });
  };

  // Mode hors-ligne
  const [horsLigne, setHorsLigne] = useState(false);
  const [dateCache, setDateCache] = useState<string | null>(null);
  const [nbVentesAttente, setNbVentesAttente] = useState(offlineService.listerVentesEnAttente().length);
  const [infoSync, setInfoSync] = useState('');

  useEffect(() => {
    chargerProduits();
    chargerClients();
    chargerRemises();
  }, []);

  // Retour du réseau : resynchroniser automatiquement les ventes en attente.
  const synchroniserVentes = useCallback(async () => {
    if (offlineService.listerVentesEnAttente().length === 0) return;
    const res = await offlineService.synchroniser();
    setNbVentesAttente(res.restantes);
    if (res.synchronisees > 0) {
      setHorsLigne(false);
      setInfoSync(`✅ ${res.synchronisees} vente(s) hors-ligne synchronisée(s)`);
      setTimeout(() => setInfoSync(''), 5000);
      chargerProduits(); // stocks réels après sync
    }
    if (res.refusees > 0) {
      setErreur(`${res.refusees} vente(s) hors-ligne refusée(s) par le serveur — voir la file d'attente`);
    }
  }, []);

  useEffect(() => {
    const surRetourReseau = () => { setHorsLigne(false); synchroniserVentes(); };
    const surCoupure = () => setHorsLigne(true);
    window.addEventListener('online', surRetourReseau);
    window.addEventListener('offline', surCoupure);
    // Tentative au montage (des ventes ont pu rester en attente d'une session précédente).
    if (navigator.onLine) synchroniserVentes();
    return () => {
      window.removeEventListener('online', surRetourReseau);
      window.removeEventListener('offline', surCoupure);
    };
  }, [synchroniserVentes]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'F1':
          e.preventDefault();
          rechercheRef.current?.focus();
          rechercheRef.current?.select();
          break;
        case 'F2':
          e.preventDefault();
          reinitialiserClient();
          break;
        case 'F10':
          e.preventDefault();
          if (panier.length > 0 && !chargement) validerVente();
          break;
        case 'F12':
          e.preventDefault();
          if (derniereVente) setShowTicket(true);
          break;
        case 'Escape':
          if (panier.length > 0) {
            const tag = (e.target as HTMLElement).tagName;
            const valeur = (e.target as HTMLInputElement).value || '';
            if (!['INPUT','TEXTAREA'].includes(tag) || valeur === '') {
              if (window.confirm('Vider le panier ?')) {
                setPanier([]);
                reinitialiserClient();
              }
            }
          }
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [panier, chargement, derniereVente]);

  const chargerProduits = async () => {
    try {
      const res = await api.get('/produits');
      setProduits(res.data);
      offlineService.sauverCache('produits', sansImages(res.data));
    } catch (e) {
      // API injoignable : repli sur le catalogue en cache (mode hors-ligne).
      const cache = offlineService.lireCache<Produit[]>('produits');
      if (cache) {
        setProduits(cache.donnees);
        setDateCache(cache.date);
        setHorsLigne(true);
      }
    }
  };

  const chargerClients = async () => {
    try {
      const res = await api.get('/clients');
      setClients(res.data);
      offlineService.sauverCache('clients', res.data);
    } catch (e) {
      const cache = offlineService.lireCache<Client[]>('clients');
      if (cache) setClients(cache.donnees);
    }
  };

  const chargerRemises = async () => {
    try {
      const res = await api.get('/configurations');
      const valeurs = {
        revendeur: parseFloat(res.data.remise_revendeur || '15'),
        technicien: parseFloat(res.data.remise_technicien || '22'),
      };
      setRemises(valeurs);
      offlineService.sauverCache('remises', valeurs);
    } catch (e) {
      const cache = offlineService.lireCache<{ revendeur: number; technicien: number }>('remises');
      if (cache) setRemises(cache.donnees);
    }
  };

  const produitsFiltres = produits.filter(p =>
    p.nom.toLowerCase().includes(rechercheProduit.toLowerCase()) ||
    p.codeBarres?.toLowerCase().includes(rechercheProduit.toLowerCase())
  );

  const clientsFiltres = clients.filter(c =>
    `${c.prenom} ${c.nom}`.toLowerCase().includes(rechercheClient.toLowerCase()) ||
    c.telephone?.includes(rechercheClient)
  ).slice(0, 6);

  const getPrixSelonType = (p: Produit) => {
    if (typeClient === 'revendeur') return Number(p.prixRevendeur);
    if (typeClient === 'technicien') return Number(p.prixTechnicien);
    return Number(p.prixOrdinaire);
  };

  const ajouterAuPanier = (p: Produit) => {
    const prix = getPrixSelonType(p);
    const existant = panier.find(l => l.produitId === p.id);
    if (existant) {
      setPanier(prev => prev.map(l =>
        l.produitId === p.id
          ? {
              ...l,
              quantite: l.quantite + 1,
              sousTotal: Math.round((l.quantite + 1) * l.prixUnitaire * (1 - l.remise / 100)),
            }
          : l
      ));
    } else {
      setPanier(prev => [...prev, {
        produitId: p.id,
        produitNom: p.nom,
        produitCode: p.codeBarres || '',
        prixUnitaire: prix,
        quantite: 1,
        remise: 0,
        sousTotal: prix,
        tvaApplicable: p.tvaApplicable,
      }]);
    }
    setRechercheProduit('');
  };

  // Ajout au SCAN / à la touche Entrée : correspondance exacte de code-barres, sinon
  // premier résultat filtré en stock. Rend l'usage d'une douchette instantané.
  const validerRecherche = () => {
    const saisie = rechercheProduit.trim();
    if (!saisie) return;
    let cible = produits.find(
      p => p.codeBarres && p.codeBarres.toLowerCase() === saisie.toLowerCase(),
    );
    if (!cible) cible = produitsFiltres.find(p => p.stockActuel > 0);
    if (cible && cible.stockActuel > 0) {
      ajouterAuPanier(cible); // vide déjà la recherche
      rechercheRef.current?.focus();
    } else if (cible) {
      setErreur(`« ${cible.nom} » est en rupture de stock`);
      setTimeout(() => setErreur(''), 2500);
    }
  };

  const modifierQuantite = (produitId: number, quantite: number) => {
    if (quantite <= 0) { supprimerDuPanier(produitId); return; }
    setPanier(prev => prev.map(l =>
      l.produitId === produitId
        ? {
            ...l,
            quantite,
            sousTotal: Math.round(quantite * l.prixUnitaire * (1 - l.remise / 100)),
          }
        : l
    ));
  };

  const modifierRemiseLigne = (produitId: number, remise: number) => {
    setPanier(prev => prev.map(l =>
      l.produitId === produitId
        ? {
            ...l,
            remise,
            sousTotal: Math.round(l.quantite * l.prixUnitaire * (1 - remise / 100)),
          }
        : l
    ));
  };

  const supprimerDuPanier = (produitId: number) => {
    setPanier(prev => prev.filter(l => l.produitId !== produitId));
  };

  const selectionnerClient = (c: Client) => {
    setClientSelectionne(c);
    setTypeClient(c.type as any);
    setModeClient('existant');
    setRechercheClient(`${c.prenom} ${c.nom}`);
    setShowClientsDropdown(false);
    recalculerPrixPanier(c.type as any);
  };

  const recalculerPrixPanier = (type: 'ordinaire' | 'revendeur' | 'technicien') => {
    setPanier(prev => prev.map(l => {
      const produit = produits.find(p => p.id === l.produitId);
      if (!produit) return l;
      const nouveauPrix = type === 'revendeur' ? Number(produit.prixRevendeur) :
        type === 'technicien' ? Number(produit.prixTechnicien) : Number(produit.prixOrdinaire);
      return {
        ...l,
        prixUnitaire: nouveauPrix,
        sousTotal: Math.round(l.quantite * nouveauPrix * (1 - l.remise / 100)),
      };
    }));
  };

  const changerTypeClient = (type: 'ordinaire' | 'revendeur' | 'technicien') => {
    setTypeClient(type);
    recalculerPrixPanier(type);
  };

  const reinitialiserClient = () => {
    setClientSelectionne(null);
    setNomClientManuel('');
    setRechercheClient('');
    setModeClient('passage');
    setTypeClient('ordinaire');
    recalculerPrixPanier('ordinaire');
  };

  // CALCULS CORRECTS
  const totalHT = Math.round(panier.reduce((s, l) => s + l.sousTotal, 0));
  const remiseMontant = Math.round(totalHT * remiseGlobale / 100);
  const totalApresRemise = totalHT - remiseMontant;
  const totalTVA = tvaActive
    ? Math.round(
        panier
          .filter(l => l.tvaApplicable)
          .reduce((s, l) => s + l.sousTotal, 0) *
        (1 - remiseGlobale / 100) *
        0.18
      )
    : 0;
  const totalTTC = totalApresRemise + totalTVA;
  const monnaie = montantRecu > 0 ? Math.round(montantRecu - totalTTC) : 0;

  const getNomClient = () => {
    if (modeClient === 'existant' && clientSelectionne) return `${clientSelectionne.prenom} ${clientSelectionne.nom}`;
    if (modeClient === 'manuel' && nomClientManuel) return nomClientManuel;
    return 'Client de passage';
  };

  // Hors-ligne : décrémente le stock affiché (le vrai décrément aura lieu à la sync).
  const decrementerStockLocal = (lignes: LignePanier[]) => {
    setProduits(prev => {
      const maj = prev.map(p => {
        const l = lignes.find(x => x.produitId === p.id);
        return l ? { ...p, stockActuel: Math.max(0, Number(p.stockActuel) - l.quantite) } : p;
      });
      offlineService.sauverCache('produits', sansImages(maj));
      return maj;
    });
  };

  const validerVente = async () => {
    if (panier.length === 0) { setErreur('Le panier est vide'); return; }
    if (modePaiement === 'credit' && (horsLigne || !navigator.onLine)) {
      setErreur('Vente à crédit indisponible hors-ligne (plafond invérifiable) — utilisez espèces ou mobile money');
      return;
    }
    if (modePaiement === 'credit' && !clientSelectionne) {
      setErreur('Sélectionnez un client existant pour une vente à crédit');
      return;
    }
    if (modePaiement === 'credit' && clientSelectionne) {
      const nouveauSolde = Number(clientSelectionne.soldeCredit) + totalTTC;
      if (clientSelectionne.plafondCredit > 0 && nouveauSolde > clientSelectionne.plafondCredit) {
        setErreur(`Plafond de crédit dépassé. Plafond : ${fmt(clientSelectionne.plafondCredit)} FCFA`);
        return;
      }
    }

    setChargement(true);
    setErreur('');

    try {
      const venteData = {
        clientId: clientSelectionne?.id || null,
        clientNom: getNomClient(),
        typeClient,
        modePaiement,
        totalHT: totalApresRemise,
        totalTVA,
        totalTTC,
        remiseGlobale,
        tvaActive,
        montantRecu: montantRecu > 0 ? montantRecu : totalTTC,
        monnaie: monnaie > 0 ? monnaie : 0,
        notes,
        statut: modePaiement === 'credit' ? 'credit' : 'payee',
        lignes: panier.map(l => ({
          produitId: l.produitId,
          produitNom: l.produitNom,
          produitCode: l.produitCode,
          prixUnitaire: l.prixUnitaire,
          quantite: l.quantite,
          remise: l.remise,
          sousTotal: l.sousTotal,
        })),
      };

      const res = await api.post('/ventes', venteData);

      // Construire l'objet vente pour le ticket
      setDerniereVente({
        id: res.data.id,
        numero: res.data.numero,
        clientNom: getNomClient(),
        typeClient,
        modePaiement,
        totalHT: totalApresRemise,
        totalTVA,
        totalTTC,
        remiseGlobale,
        tvaActive,
        montantRecu: montantRecu > 0 ? montantRecu : totalTTC,
        monnaie: monnaie > 0 ? monnaie : 0,
        statut: modePaiement === 'credit' ? 'credit' : 'payee',
        notes,
        createdAt: res.data.createdAt || new Date().toISOString(),
        lignes: panier.map(l => ({
          produitNom: l.produitNom,
          quantite: l.quantite,
          prixUnitaire: l.prixUnitaire,
          remise: l.remise,
          sousTotal: l.sousTotal,
        })),
      });

      setImprimerAuto(autoImprimer);
      setShowTicket(true);
      setPanier([]);
      reinitialiserClient();
      setMontantRecu(0);
      setRemiseGlobale(0);
      setNotes('');
      setModePaiement('especes');
    } catch (e: any) {
      // Coupure réseau (pas de réponse serveur) : la vente part en file d'attente
      // locale — sauf le crédit, déjà bloqué en amont.
      if (estErreurReseau(e) && modePaiement !== 'credit') {
        const venteData = {
          clientId: clientSelectionne?.id || null,
          clientNom: getNomClient(),
          typeClient,
          modePaiement,
          totalHT: totalApresRemise,
          totalTVA,
          totalTTC,
          remiseGlobale,
          tvaActive,
          montantRecu: montantRecu > 0 ? montantRecu : totalTTC,
          monnaie: monnaie > 0 ? monnaie : 0,
          notes,
          statut: 'payee',
          lignes: panier.map(l => ({
            produitId: l.produitId,
            produitNom: l.produitNom,
            produitCode: l.produitCode,
            prixUnitaire: l.prixUnitaire,
            quantite: l.quantite,
            remise: l.remise,
            sousTotal: l.sousTotal,
          })),
        };
        const enAttente = offlineService.mettreEnAttente(venteData);
        setNbVentesAttente(offlineService.listerVentesEnAttente().length);
        setHorsLigne(true);
        decrementerStockLocal(panier);

        // Ticket immédiat avec numéro provisoire (le vrai numéro viendra à la sync).
        setDerniereVente({
          id: 0,
          numero: enAttente.numeroProvisoire,
          clientNom: getNomClient(),
          typeClient,
          modePaiement,
          totalHT: totalApresRemise,
          totalTVA,
          totalTTC,
          remiseGlobale,
          tvaActive,
          montantRecu: montantRecu > 0 ? montantRecu : totalTTC,
          monnaie: monnaie > 0 ? monnaie : 0,
          statut: 'payee',
          notes,
          createdAt: enAttente.creeLe,
          lignes: panier.map(l => ({
            produitNom: l.produitNom,
            quantite: l.quantite,
            prixUnitaire: l.prixUnitaire,
            remise: l.remise,
            sousTotal: l.sousTotal,
          })),
        });
        setImprimerAuto(autoImprimer);
        setShowTicket(true);
        setPanier([]);
        reinitialiserClient();
        setMontantRecu(0);
        setRemiseGlobale(0);
        setNotes('');
        setModePaiement('especes');
        setInfoSync('📴 Vente enregistrée HORS-LIGNE — elle sera synchronisée au retour du réseau');
        setTimeout(() => setInfoSync(''), 6000);
      } else {
        setErreur(e.response?.data?.message || 'Erreur lors de la vente');
      }
    } finally {
      setChargement(false);
    }
  };

  return (
    <div className="caisse-page">
      {/* BANDEAU MODE HORS-LIGNE / SYNCHRONISATION */}
      {(horsLigne || nbVentesAttente > 0) && (
        <div className={`caisse-bandeau ${horsLigne ? 'hors-ligne' : 'attente-sync'}`}>
          {horsLigne ? (
            <>
              📴 <strong>Mode hors-ligne</strong>
              {dateCache && <> — catalogue du {new Date(dateCache).toLocaleString('fr-FR')}</>}
              {nbVentesAttente > 0 && <> • {nbVentesAttente} vente(s) en attente</>}
              <span className="bandeau-note"> (espèces / mobile money uniquement)</span>
            </>
          ) : (
            <>
              🔄 {nbVentesAttente} vente(s) en attente de synchronisation
              <button className="bandeau-btn" onClick={synchroniserVentes}>Synchroniser maintenant</button>
            </>
          )}
        </div>
      )}
      {infoSync && <div className="caisse-bandeau info-sync">{infoSync}</div>}
      <div className="caisse-layout">

        {/* PANNEAU GAUCHE — PRODUITS */}
        <div className="caisse-produits">
          <div className="caisse-section-titre">🔍 Rechercher un produit</div>
          <input
            ref={rechercheRef}
            className="caisse-search"
            value={rechercheProduit}
            onChange={e => setRechercheProduit(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); validerRecherche(); } }}
            placeholder="Scannez ou tapez un produit, puis Entrée"
            autoFocus
          />
          <div className="produits-liste-caisse">
            {produitsFiltres.slice(0, 12).map(p => (
              <div key={p.id}
                className={`produit-caisse-item ${p.stockActuel === 0 ? 'rupture' : ''}`}
                onClick={() => p.stockActuel > 0 && ajouterAuPanier(p)}>
                {p.image && <img className="pci-thumb" src={p.image} alt={p.nom} />}
                <div className="pci-corps">
                  <div className="pci-nom">{p.nom}</div>
                  <div className="pci-bas">
                    <span className="pci-stock">Stock: {p.stockActuel}</span>
                    <span className="pci-prix">{fmt(getPrixSelonType(p))} F</span>
                  </div>
                </div>
              </div>
            ))}
            {produitsFiltres.length === 0 && rechercheProduit && (
              <div style={{ textAlign: 'center', color: '#888', padding: '20px', fontSize: 12 }}>
                Aucun produit trouvé
              </div>
            )}
          </div>
        </div>

        {/* PANNEAU CENTRAL — PANIER */}
        <div className="caisse-panier">
          <div className="caisse-section-titre">🛒 Panier</div>
          {erreur && <div className="alert-erreur">⚠️ {erreur}</div>}

          {/* CLIENT */}
          <div className="client-section">
            {/* Client — ligne compacte, détails uniquement à la demande */}
            <div className="client-compact">
              <span className="cc-nom">
                👤 {getNomClient()}
                {typeClient !== 'ordinaire' && <span className="cc-type"> · {typeClient}</span>}
                {clientSelectionne && Number(clientSelectionne.soldeCredit) > 0 && (
                  <span className="cc-credit"> · crédit {fmt(clientSelectionne.soldeCredit)} F</span>
                )}
              </span>
              <button className="cc-changer" onClick={() => setShowClientMenu(v => !v)}>
                Changer ▾
              </button>
            </div>

            {showClientMenu && (
              <div className="client-menu">
                <button onClick={() => { reinitialiserClient(); setModeClient('passage'); setShowClientMenu(false); }}>🚶 De passage</button>
                <button onClick={() => { reinitialiserClient(); setModeClient('manuel'); setShowClientMenu(false); }}>✍️ Saisir un nom</button>
                <button onClick={() => { reinitialiserClient(); setModeClient('existant'); setShowClientMenu(false); }}>🔍 Client existant</button>
              </div>
            )}

            {modeClient === 'manuel' && (
              <div className="client-manuel">
                <input
                  value={nomClientManuel}
                  onChange={e => setNomClientManuel(e.target.value)}
                  placeholder="Nom du client..."
                  className="client-manuel-input"
                  autoFocus
                />
              </div>
            )}

            {modeClient === 'existant' && !clientSelectionne && (
              <div className="client-existant-section">
                <input
                  className="caisse-search"
                  value={rechercheClient}
                  onChange={e => { setRechercheClient(e.target.value); setShowClientsDropdown(true); }}
                  onFocus={() => setShowClientsDropdown(true)}
                  placeholder="Rechercher par nom ou téléphone..."
                  autoFocus
                />
                {showClientsDropdown && rechercheClient && (
                  <div className="clients-dropdown">
                    {clientsFiltres.length === 0 ? (
                      <div className="cd-vide">Aucun client trouvé</div>
                    ) : (
                      clientsFiltres.map(c => (
                        <div key={c.id} className="cd-item" onClick={() => selectionnerClient(c)}>
                          <div className="cd-nom">{c.prenom} {c.nom}</div>
                          <div className="cd-tel">{c.telephone} — {c.type}</div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Tarif — sélecteur segmenté compact */}
            <div className="tarif-seg">
              {[
                { val: 'ordinaire', label: 'Ordinaire', remise: '' },
                { val: 'revendeur', label: 'Revendeur', remise: `-${remises.revendeur}%` },
                { val: 'technicien', label: 'Technicien', remise: `-${remises.technicien}%` },
              ].map(t => (
                <button key={t.val}
                  className={typeClient === t.val ? 'active' : ''}
                  onClick={() => changerTypeClient(t.val as any)}>
                  {t.label}{t.remise && <span className="ts-remise">{t.remise}</span>}
                </button>
              ))}
            </div>
          </div>

          {/* LIGNES PANIER */}
          <div className="panier-lignes">
            {panier.length === 0 ? (
              <div className="panier-vide">
                <div>🛒</div>
                <p>Panier vide — ajoutez des produits</p>
              </div>
            ) : (
              panier.map(l => {
                const remiseOuverte = remisesOuvertes.has(l.produitId) || l.remise > 0;
                return (
                  <div key={l.produitId} className="panier-ligne">
                    <div className="pl-top">
                      <div className="pl-nom">{l.produitNom}</div>
                      <button className="pl-suppr"
                        onClick={() => supprimerDuPanier(l.produitId)}>✕</button>
                    </div>
                    <div className="pl-controles">
                      <div className="pl-qte">
                        <button onClick={() => modifierQuantite(l.produitId, l.quantite - 1)}>−</button>
                        <InputNombre
                          value={l.quantite}
                          onChange={val => modifierQuantite(l.produitId, val)}
                          min={1}
                          placeholder="1"
                        />
                        <button onClick={() => modifierQuantite(l.produitId, l.quantite + 1)}>+</button>
                      </div>
                      <div className="pl-prix">{fmt(l.prixUnitaire)} F</div>
                      <button
                        className={`pl-remise-btn ${l.remise > 0 ? 'actif' : ''}`}
                        title="Remise sur cette ligne"
                        onClick={() => basculerRemiseLigne(l.produitId)}>
                        {l.remise > 0 ? `-${l.remise}%` : '%'}
                      </button>
                      <div className="pl-total">{fmt(l.sousTotal)} F</div>
                    </div>
                    {remiseOuverte && (
                      <div className="pl-remise-row">
                        <span>Remise sur la ligne</span>
                        <div className="pl-remise">
                          <InputNombre
                            value={l.remise}
                            onChange={val => modifierRemiseLigne(l.produitId, val)}
                            placeholder="0"
                            min={0}
                            max={100}
                          />
                          <span>%</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* TOTAUX (lecture seule — réglages dans « Options ») */}
          {panier.length > 0 && (
            <div className="panier-totaux">
              <div className="pt-row">
                <span>Sous-total</span>
                <span>{fmt(totalHT)} FCFA</span>
              </div>
              {remiseGlobale > 0 && (
                <div className="pt-row" style={{ color: '#A32D2D' }}>
                  <span>Remise {remiseGlobale}%</span>
                  <span>-{fmt(remiseMontant)} FCFA</span>
                </div>
              )}
              {tvaActive && (
                <div className="pt-row">
                  <span>TVA 18%</span>
                  <span>{fmt(totalTVA)} FCFA</span>
                </div>
              )}
              <div className="pt-row total">
                <span>TOTAL</span>
                <span>{fmt(totalTTC)} FCFA</span>
              </div>
            </div>
          )}
        </div>

        {/* PANNEAU DROIT — PAIEMENT */}
        <div className="caisse-paiement">
          <div className="caisse-section-titre">💳 Paiement</div>

          <div className="modes-paiement">
            {MODES_PAIEMENT.map(m => {
              const creditBloque = m.val === 'credit' && horsLigne;
              return (
                <button key={m.val}
                  className={`mode-btn ${modePaiement === m.val ? 'active' : ''} ${creditBloque ? 'indisponible' : ''}`}
                  disabled={creditBloque}
                  title={creditBloque ? 'Crédit indisponible hors-ligne' : undefined}
                  onClick={() => setModePaiement(m.val)}>
                  {m.label}
                </button>
              );
            })}
          </div>

          {modePaiement === 'especes' && (
            <div className="montant-recu-section">
              <label>Montant reçu (FCFA)</label>
              <InputNombre
                value={montantRecu}
                onChange={val => setMontantRecu(val)}
                placeholder={fmt(totalTTC)}
              />
              {/* Boutons d'appoint : cumulent les coupures reçues (tactile + rapidité). */}
              <div className="appoint">
                {[500, 1000, 2000, 5000, 10000].map(v => (
                  <button key={v} type="button"
                    onClick={() => setMontantRecu(m => (Number(m) || 0) + v)}>
                    +{fmt(v)}
                  </button>
                ))}
                <button type="button" className="appoint-exact"
                  onClick={() => setMontantRecu(totalTTC)}>Compte juste</button>
                <button type="button" className="appoint-clear"
                  onClick={() => setMontantRecu(0)}>Effacer</button>
              </div>
              {montantRecu > 0 && montantRecu >= totalTTC && (
                <div className="monnaie-rendue">
                  Monnaie à rendre : <strong>{fmt(monnaie)} FCFA</strong>
                </div>
              )}
            </div>
          )}

          {modePaiement === 'credit' && !clientSelectionne && (
            <div className="credit-warning">
              ⚠️ Sélectionnez un client existant pour une vente à crédit
            </div>
          )}

          {modePaiement === 'credit' && clientSelectionne && (
            <div className="credit-info">
              <div>Crédit actuel : {fmt(clientSelectionne.soldeCredit)} FCFA</div>
              <div>Après vente : {fmt(Number(clientSelectionne.soldeCredit) + totalTTC)} FCFA</div>
              {clientSelectionne.plafondCredit > 0 && (
                <div>Plafond : {fmt(clientSelectionne.plafondCredit)} FCFA</div>
              )}
            </div>
          )}

          <div className="recap-vente">
            <div className="rv-client">
              👤 {getNomClient()}
              {typeClient !== 'ordinaire' && (
                <span className="rv-type"> — {typeClient}</span>
              )}
            </div>
            <div className="rv-montant">{fmt(totalTTC)} FCFA</div>
            <div className="rv-produits">
              {panier.length} produit{panier.length > 1 ? 's' : ''}
            </div>
          </div>

          <button
            className="btn-valider"
            onClick={validerVente}
            disabled={chargement || panier.length === 0}
            title="F10">
            {chargement ? '⏳ Traitement...' : `✅ Valider — ${fmt(totalTTC)} FCFA`}
          </button>

          <button className="btn-annuler" title="Échap" onClick={() => {
            if (panier.length > 0 && window.confirm('Vider le panier ?')) {
              setPanier([]);
              reinitialiserClient();
            }
          }}>
            🗑️ Vider le panier
          </button>

          {/* OPTIONS AVANCÉES (repliées par défaut pour désencombrer) */}
          <button className="options-toggle" onClick={() => setShowOptions(v => !v)}>
            ⚙️ Options {showOptions ? '▲' : '▼'}
          </button>
          {showOptions && (
            <div className="options-panel">
              <div className="opt-row">
                <span>Remise globale</span>
                <div className="pt-remise-input">
                  <InputNombre
                    value={remiseGlobale}
                    onChange={val => setRemiseGlobale(Math.min(50, Math.max(0, val)))}
                    placeholder="0" min={0} max={50}
                  />
                  <span>%</span>
                </div>
              </div>
              <label className="opt-check">
                <input type="checkbox" checked={tvaActive}
                  onChange={e => setTvaActive(e.target.checked)} />
                <span>Appliquer la TVA (18%)</span>
              </label>
              <label className="opt-check">
                <input type="checkbox" checked={autoImprimer}
                  onChange={e => changerAutoImprimer(e.target.checked)} />
                <span>🖨️ Imprimer automatiquement le ticket</span>
              </label>
              <div className="opt-notes">
                <label>Note (optionnel)</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={2}
                  placeholder="Observation sur la vente..."
                />
              </div>
              <button className="btn-raccourcis" onClick={() => setShowRaccourcis(v => !v)}>
                ⌨️ Raccourcis clavier
              </button>
              {showRaccourcis && (
                <div className="raccourcis-panel">
                  <div className="raccourcis-titre">Raccourcis clavier</div>
                  {[
                    { key: 'F1',    desc: 'Focus recherche produit' },
                    { key: 'F2',    desc: 'Réinitialiser le client' },
                    { key: 'F10',   desc: 'Valider la vente' },
                    { key: 'F12',   desc: 'Réimprimer le dernier ticket' },
                    { key: 'Échap', desc: 'Vider le panier' },
                  ].map(r => (
                    <div key={r.key} className="raccourci-ligne">
                      <span className="raccourci-key">{r.key}</span>
                      <span className="raccourci-desc">{r.desc}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* TICKET VENTE */}
      {showTicket && derniereVente && (
        <TicketVente
          vente={derniereVente}
          autoImprimer={imprimerAuto}
          onFermer={() => {
            setShowTicket(false);
            setImprimerAuto(false);
          }}
        />
      )}
    </div>
  );
}